import { useEffect, useMemo, useRef, useState } from 'react';
import { CircleAlert, CircleCheck, Eye, RotateCcw, Upload } from 'lucide-react';
import logoCharte from '../../assets/top-formation.png';
import { Carte, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { dateReunion } from '../../lib/useFormateurs';
import { useValeurDifferee } from '../../lib/useRecherche';
import {
  estCouleur,
  useApercuPalette,
  useAppliquerMarque,
  type CleCouleur,
  type CouleursMarque,
  type VueMarque,
} from '../../lib/useParametrage';
import { useMarque } from '../../marque/useMarque';
import type { MarquePublique } from '../../marque/marque';
import { ApercuInterface } from './ApercuInterface';
import { SectionDomaine } from './SectionDomaine';
import { SectionLinkedin } from './SectionLinkedin';
import { CLASSE_BOUTON_ENREGISTRER, messageErreur } from './commun';

/** Ce que la direction prépare, avant de l'appliquer. */
interface Brouillon {
  nom: string;
  couleurs: CouleursMarque;
  logo: 'CONSERVER' | 'RETIRER' | 'REMPLACER';
  fichier: File | null;
  /** Adresse locale du fichier choisi, pour l'aperçu. */
  urlFichier: string | null;
}

function brouillonDe(vue: VueMarque): Brouillon {
  const couleurs = Object.fromEntries(vue.couleurs.map((couleur) => [couleur.cle, couleur.valeur])) as CouleursMarque;
  return { nom: vue.nom.valeur, couleurs, logo: 'CONSERVER', fichier: null, urlFichier: null };
}

function estBrouillon(valeur: unknown): valeur is Brouillon {
  return typeof valeur === 'object' && valeur !== null && 'nom' in valeur && 'couleurs' in valeur && 'logo' in valeur;
}

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

const CLASSE_BOUTON_SECONDAIRE =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-50';

/**
 * Marque blanche (TF-436) : nom affiché, logo et couleurs de toute
 * l'interface et des e-mails. Rien ne change pour personne avant
 * « Appliquer » : l'aperçu se regarde ici, ou sur toute la plateforme pour la
 * seule personne qui règle. Les contrastes sont calculés par l'API, qui
 * refuse une palette illisible.
 */
export function OngletMarque({ marque }: { marque: VueMarque }): JSX.Element {
  const toast = useToast();
  const appliquer = useAppliquerMarque();
  const { apercu, demarrerApercu, arreterApercu } = useMarque();
  const [brouillon, setBrouillon] = useState<Brouillon>(() =>
    estBrouillon(apercu?.brouillon) ? apercu.brouillon : brouillonDe(marque),
  );
  const [erreur, setErreur] = useState<string | null>(null);
  const champFichier = useRef<HTMLInputElement>(null);

  const couleursValides = useMemo<CouleursMarque | null>(() => {
    const { principale, secondaire, accent } = brouillon.couleurs;
    if (![principale, secondaire, accent].every(estCouleur)) return null;
    return {
      principale: principale.trim().toUpperCase(),
      secondaire: secondaire.trim().toUpperCase(),
      accent: accent.trim().toUpperCase(),
    };
  }, [brouillon.couleurs]);
  const cleDifferee = useValeurDifferee(couleursValides ? JSON.stringify(couleursValides) : '', 250);
  const couleursDifferees = useMemo<CouleursMarque | null>(
    () => (cleDifferee ? (JSON.parse(cleDifferee) as CouleursMarque) : null),
    [cleDifferee],
  );
  const apercuPalette = useApercuPalette(couleursDifferees);
  const palette = apercuPalette.data ?? marque.palette;

  const urlLogo =
    brouillon.logo === 'REMPLACER' ? brouillon.urlFichier : brouillon.logo === 'RETIRER' ? null : (marque.logo?.url ?? null);
  const marqueApercue = useMemo<MarquePublique>(
    () => ({
      nom: brouillon.nom.trim() || marque.nom.charte,
      nuances: palette.nuances,
      logo: urlLogo ? { url: urlLogo } : null,
    }),
    [brouillon.nom, marque.nom.charte, palette.nuances, urlLogo],
  );

  // L'aperçu sur toute la plateforme suit la saisie, tant qu'il est ouvert.
  const apercuOuvert = apercu !== null;
  useEffect(() => {
    if (apercuOuvert) demarrerApercu({ marque: marqueApercue, brouillon });
  }, [apercuOuvert, marqueApercue, brouillon, demarrerApercu]);

  const enVigueur = brouillonDe(marque);
  const modifie =
    brouillon.nom.trim() !== enVigueur.nom ||
    brouillon.logo !== 'CONSERVER' ||
    (Object.keys(enVigueur.couleurs) as CleCouleur[]).some(
      (cle) => brouillon.couleurs[cle].trim().toUpperCase() !== enVigueur.couleurs[cle],
    );
  const calculEnCours = couleursValides !== null && cleDifferee !== JSON.stringify(couleursValides);
  const refus = !couleursValides
    ? 'Chaque couleur s’écrit au format #RRGGBB.'
    : calculEnCours || apercuPalette.isFetching
      ? null
      : palette.refus;
  const nomTrop = brouillon.nom.trim().length < marque.nom.min || brouillon.nom.trim().length > marque.nom.max;

  const changer = (modification: Partial<Brouillon>): void => {
    setErreur(null);
    setBrouillon((avant) => ({ ...avant, ...modification }));
  };
  const changerCouleur = (cle: CleCouleur, valeur: string): void =>
    changer({ couleurs: { ...brouillon.couleurs, [cle]: valeur } });

  const choisirFichier = (fichier: File | undefined): void => {
    if (!fichier) return;
    if (brouillon.urlFichier) URL.revokeObjectURL(brouillon.urlFichier);
    changer({ logo: 'REMPLACER', fichier, urlFichier: URL.createObjectURL(fichier) });
  };

  const repartirDeLaCharte = (): void =>
    changer({
      nom: marque.charte.nom,
      couleurs: { ...marque.charte.couleurs },
      logo: marque.logo ? 'RETIRER' : 'CONSERVER',
      fichier: null,
      urlFichier: null,
    });

  const annuler = (): void => {
    setErreur(null);
    setBrouillon(brouillonDe(marque));
    if (apercuOuvert) arreterApercu();
  };

  const envoyer = (): void => {
    if (!couleursValides) return;
    setErreur(null);
    appliquer.mutate(
      { nom: brouillon.nom, couleurs: couleursValides, logo: brouillon.logo, fichier: brouillon.fichier },
      {
        onSuccess: (vue) => {
          arreterApercu();
          setBrouillon(brouillonDe(vue));
          toast('Marque appliquée à toute la plateforme');
        },
        onError: (refusApi) => setErreur(messageErreur(refusApi, 'La marque n’a pas pu être appliquée.')),
      },
    );
  };

  const peutAppliquer =
    modifie && !refus && !nomTrop && !calculEnCours && !apercuPalette.isFetching && !appliquer.isPending;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <Carte titre="Identité de la plateforme">
          <div className="space-y-5 px-4 py-4 sm:px-6">
            <p className="text-xs text-codialis-gray">
              Le nom, le logo et les couleurs habillent tous les espaces — apprenant, formateur, pôles, direction —, les
              pages publiques (connexion, préinscription, questionnaires) et les e-mails. Les documents PDF gardent les
              modèles du client, et le nom légal y reste celui de l’organisme.
            </p>

            <div>
              <label htmlFor="marque-nom" className="text-xs font-semibold text-codialis-navy">
                Nom affiché
              </label>
              <input
                id="marque-nom"
                type="text"
                value={brouillon.nom}
                maxLength={marque.nom.max}
                onChange={(evenement) => changer({ nom: evenement.target.value })}
                className={`${CLASSE_CHAMP} mt-1`}
              />
              <p className="mt-1 text-[11px] text-codialis-gray">
                {nomTrop
                  ? `De ${marque.nom.min} à ${marque.nom.max} caractères.`
                  : `Menu, titre de l’onglet du navigateur, page de connexion et e-mails. Charte : ${marque.nom.charte}.`}
              </p>
            </div>

            <div>
              <div className="text-xs font-semibold text-codialis-navy">Logo</div>
              <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex h-20 w-full shrink-0 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white p-3 sm:w-48">
                  <img
                    src={urlLogo ?? logoCharte}
                    alt={`Logo : ${marqueApercue.nom}`}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="min-w-0 space-y-2">
                  <input
                    ref={champFichier}
                    type="file"
                    accept="image/png,image/jpeg"
                    className="sr-only"
                    aria-label="Choisir un logo"
                    onChange={(evenement) => {
                      choisirFichier(evenement.target.files?.[0]);
                      evenement.target.value = '';
                    }}
                  />
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className={CLASSE_BOUTON_SECONDAIRE} onClick={() => champFichier.current?.click()}>
                      <Upload className="h-4 w-4" aria-hidden="true" />
                      Choisir un logo
                    </button>
                    {(marque.logo || brouillon.logo === 'REMPLACER') && brouillon.logo !== 'RETIRER' && (
                      <button
                        type="button"
                        className={CLASSE_BOUTON_SECONDAIRE}
                        onClick={() =>
                          changer({ logo: marque.logo ? 'RETIRER' : 'CONSERVER', fichier: null, urlFichier: null })
                        }
                      >
                        Logo de la charte
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-codialis-gray">
                    {brouillon.logo === 'REMPLACER' && brouillon.fichier
                      ? `Nouveau logo : ${brouillon.fichier.name}, appliqué avec le reste.`
                      : brouillon.logo === 'RETIRER'
                        ? 'Le logo de la charte reprendra sa place.'
                        : marque.logo
                          ? `Déposé${marque.logo.deposePar ? ` par ${marque.logo.deposePar}` : ''} le ${dateReunion(marque.logo.deposeLe)}.`
                          : 'Logo de la charte.'}{' '}
                    {marque.formatsLogo}, sur fond clair.
                  </p>
                </div>
              </div>
            </div>

            <fieldset className="space-y-3">
              <legend className="text-xs font-semibold text-codialis-navy">Couleurs</legend>
              {marque.couleurs.map((couleur) => {
                const valeur = brouillon.couleurs[couleur.cle];
                return (
                  <div key={couleur.cle} className="flex items-start gap-3">
                    <input
                      type="color"
                      aria-label={`${couleur.libelle} : nuancier`}
                      value={estCouleur(valeur) ? valeur.trim().toLowerCase() : couleur.charte.toLowerCase()}
                      onChange={(evenement) => changerCouleur(couleur.cle, evenement.target.value.toUpperCase())}
                      className="h-11 w-11 shrink-0 cursor-pointer rounded-lg border border-gray-200 bg-white p-1"
                    />
                    <div className="min-w-0 flex-1">
                      <label htmlFor={`marque-${couleur.cle}`} className="text-xs font-semibold text-codialis-navy">
                        {couleur.libelle}
                      </label>
                      <input
                        id={`marque-${couleur.cle}`}
                        type="text"
                        value={valeur}
                        maxLength={7}
                        spellCheck={false}
                        onChange={(evenement) => changerCouleur(couleur.cle, evenement.target.value)}
                        className={`${CLASSE_CHAMP} mt-1 font-mono uppercase`}
                      />
                      <p className="mt-1 text-[11px] text-codialis-gray">
                        {couleur.usage}. Charte : {couleur.charte}.
                      </p>
                    </div>
                  </div>
                );
              })}
            </fieldset>

            <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
              <button type="button" className={CLASSE_BOUTON_SECONDAIRE} onClick={repartirDeLaCharte}>
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                Repartir de la charte d’origine
              </button>
              {modifie && (
                <button type="button" className={CLASSE_BOUTON_SECONDAIRE} onClick={annuler}>
                  Annuler les modifications
                </button>
              )}
            </div>
            <p className="text-[11px] text-codialis-gray">
              {marque.estCharte
                ? 'En vigueur : la charte d’origine.'
                : `En vigueur depuis le ${marque.modifieLe ? dateReunion(marque.modifieLe) : '—'}${
                    marque.modifiePar ? `, réglée par ${marque.modifiePar}` : ''
                  }.`}
            </p>
          </div>
        </Carte>

        <Carte titre="Aperçu avant application">
          <div className="space-y-4 px-4 py-4 sm:px-6">
            <ApercuInterface marque={marqueApercue} />

            <div>
              <div className="text-xs font-semibold text-codialis-navy">Lisibilité</div>
              <ul className="mt-2 divide-y divide-gray-100 rounded-xl border border-gray-100">
                {palette.contrastes.map((controle) => (
                  <li key={controle.cle} className="flex items-center gap-3 px-3 py-2">
                    {controle.conforme ? (
                      <CircleCheck className="h-4 w-4 shrink-0 text-emerald-600" aria-label="Conforme" />
                    ) : (
                      <CircleAlert className="h-4 w-4 shrink-0 text-red-600" aria-label="Insuffisant" />
                    )}
                    <span className="min-w-0 flex-1 text-xs text-codialis-navy">{controle.libelle}</span>
                    <span className="shrink-0 text-right font-mono text-[11px] text-codialis-gray">
                      {controle.texte} · {controle.minimum.toLocaleString('fr-FR')}:1 au moins
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {refus && <MessageErreur message={refus} />}
            {erreur && <MessageErreur message={erreur} />}

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              {apercuOuvert ? (
                <button type="button" className={CLASSE_BOUTON_SECONDAIRE} onClick={arreterApercu}>
                  Quitter l’aperçu sur la plateforme
                </button>
              ) : (
                <button
                  type="button"
                  className={CLASSE_BOUTON_SECONDAIRE}
                  disabled={!modifie || Boolean(refus) || nomTrop}
                  onClick={() => demarrerApercu({ marque: marqueApercue, brouillon })}
                >
                  <Eye className="h-4 w-4" aria-hidden="true" />
                  Prévisualiser sur toute la plateforme
                </button>
              )}
              <button type="button" className={CLASSE_BOUTON_ENREGISTRER} disabled={!peutAppliquer} onClick={envoyer}>
                {appliquer.isPending ? 'Application…' : 'Appliquer à toute la plateforme'}
              </button>
            </div>
            <p className="text-[11px] text-codialis-gray">
              L’aperçu sur toute la plateforme n’est visible que de vous : vous parcourez les écrans habillés du
              brouillon, sans rien appliquer. Un rechargement de la page y met fin.
            </p>
          </div>
        </Carte>
      </div>

      <SectionDomaine domaine={marque.domaine} />
      <SectionLinkedin linkedin={marque.linkedin} nom={marque.nom.valeur} />
    </div>
  );
}
